<template>
  <div>
    <div class="flex flex-col gap-6 pb-12">
      <div class="flex flex-col pt-12">
        <span class="text-xs font-semibold text-gray-400 md:text-2xl"> Novidade! </span>
        <h2 class="text-3xl font-bold text-gray-50 md:text-5xl">OnTick Best</h2>
        <span class="pt-4 text-sm md:text-base">
          Navegue as tendências do mercado e transforme seus insights em potencial de lucro!
        </span>
      </div>
      <div class="flex items-center">
        <router-link
          :to="{ name: 'Ranking OnTick Best' }"
          v-tippy="{ arrow: true, theme: 'green' }"
          content="Ranking OnTick Best"
          class="h-full px-2 flex items-center hover:brightness-125 active:brightness-75 transition-all"
        >
          <i v-html="$icons.best_ranking" class="translate-y-1"></i>
          <span class="sr-only"> Ranking OnTick Best </span>
        </router-link>
        <button
          v-for="(tab, index) in tabs"
          :key="index"
          class="rounded-full border-b-0 px-3 py-2 text-xs font-normal text-gray-300 md:rounded-none md:bg-transparent md:py-3 md:text-base"
          :class="{
            'border-ontick-blue-600 bg-ontick-blue-900 !font-semibold !text-gray-50 md:border-b':
              selected_tab == tabs[index],
          }"
          @click="selected_tab = tab"
        >
          {{ tab }}
        </button>
      </div>

      <div class="flex w-full items-center justify-center py-12 md:hidden" v-if="is_loading">
        <i v-html="$icons.loading"></i>
        <span class="sr-only">Carregando</span>
      </div>

      <best-available-grid-component class="md:hidden" v-else-if="selected_tab == 'Disponíveis'" />
      <best-mobile-running-component class="md:hidden" v-else-if="selected_tab == 'Em andamento'" />
      <best-mobile-history-component class="md:hidden" v-else />

      <best-table-component
        id="bests-table"
        class="hidden md:block"
        :available="getAvailableBests"
        :running="getUserBests"
        :history="history"
        :selected_tab="selected_tab"
        :is_loading="is_loading"
      />

      <div v-if="selected_tab == 'Histórico'" class="flex w-full items-center gap-2">
        <button class="pagination btn" :disabled="history_page == 1" @click="previousPage">
          <span class="sr-only"> Página anterior </span>
          <i v-html="$icons.chevron_left"></i>
        </button>
        <div class="pagination">
          <span> {{ history_page }} </span>
        </div>
        <button class="pagination btn" :disabled="history_page_is_empty" @click="nextPage">
          <span class="sr-only"> Próxima página </span>
          <i v-html="$icons.chevron_right"></i>
        </button>
      </div>

      <p class="hidden pt-6 text-gray-400 lg:block">
        Investimentos e estratégias de investimento apresentam riscos potenciais e retornos. Desempenhos passados não
        são indicativos de resultados futuros. Nós não garantimos qualquer resultado específico ou lucro. Você deve
        estar ciente dos riscos e estar disposto a aceitá-los para investir nos mercados. Qualquer decisão de
        investimento que você tome deve ser baseada em sua própria diligência e consideração de suas circunstâncias
        pessoais, incluindo, mas não se limitando a, sua tolerância ao risco, situação financeira e objetivos de
        investimento. Nós não nos responsabilizamos por quaisquer perdas que você possa sofrer como resultado do uso das
        informações, dados ou serviços da plataforma. Ao utilizar nosso site e nossos serviços, você concorda com este
        aviso de isenção de responsabilidade e assume total responsabilidade por suas decisões e ações.
      </p>
    </div>
    <portal to="modal">
      <best-start-modal-component
        v-if="start_modal_is_active"
        :data="start_modal_data"
        @close="start_modal_is_active = false"
        @reload="setBests(getSelectedAccount.account_id)"
        @change-tab="selected_tab = 'Em andamento'"
      />
      <best-stop-modal-component
        v-if="stop_modal_is_active"
        :data="stop_modal_data"
        @close="stop_modal_is_active = false"
        @reload="setBests(getSelectedAccount.account_id)"
        @change-tab="selected_tab = 'Disponíveis'"
      />
    </portal>
    <div
      class="bg-ontick-steel-500 ease fixed left-0 z-10 flex h-screen w-full translate-y-full flex-col transition-all duration-500 lg:hidden"
      :class="disclaimer_is_active ? 'bottom-0 translate-y-0' : 'bottom-[96px] translate-y-full lg:bottom-8'"
    >
      <button
        class="flex w-full items-center justify-center pt-1 pb-6 lg:pl-72"
        @click="disclaimer_is_active = !disclaimer_is_active"
      >
        <i v-html="$icons.chevron_left" :class="disclaimer_is_active ? '-rotate-90' : 'rotate-90'"></i>
      </button>
      <p class="px-12 text-xs lg:pl-72">
        Investimentos e estratégias de investimento apresentam riscos potenciais e retornos. Desempenhos passados não
        são indicativos de resultados futuros. Nós não garantimos qualquer resultado específico ou lucro. Você deve
        estar ciente dos riscos e estar disposto a aceitá-los para investir nos mercados. Qualquer decisão de
        investimento que você tome deve ser baseada em sua própria diligência e consideração de suas circunstâncias
        pessoais, incluindo, mas não se limitando a, sua tolerância ao risco, situação financeira e objetivos de
        investimento. Nós não nos responsabilizamos por quaisquer perdas que você possa sofrer como resultado do uso das
        informações, dados ou serviços da plataforma. Ao utilizar nosso site e nossos serviços, você concorda com este
        aviso de isenção de responsabilidade e assume total responsabilidade por suas decisões e ações.
      </p>
    </div>
    <portal to="modal">
      <ontick-modal
        bgClass="dark:bg-ontick-carbon-500 bg-gray-50"
        :open="trend_modal_is_active"
        @close="trend_modal_is_active = false"
        size="small"
      >
        <best-trend-modal />
      </ontick-modal>
    </portal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import event_bus from "../../../shared/utils/event_bus";
import BestAvailableGridComponent from "../components/best-available-grid.component.vue";
import bestMobileHistoryComponent from "../components/best-mobile-history.component.vue";
import bestMobileRunningComponent from "../components/best-mobile-running.component.vue";
import BestStartModalComponent from "../components/best-start-modal.component.vue";
import BestStopModalComponent from "../components/best-stop-modal.component.vue";
import bestTableComponent from "../components/best-table.component.vue";
import bestTrendModal from "../components/best-trend-modal.component.vue";
import ontickModal from "../../../shared/components/basic/ontick-modal.component.vue";
export default {
  components: {
    bestTableComponent,
    BestStartModalComponent,
    BestStopModalComponent,
    BestAvailableGridComponent,
    bestMobileRunningComponent,
    bestMobileHistoryComponent,
    ontickModal,
    bestTrendModal,
  },
  data() {
    return {
      tabs: ["Disponíveis", "Em andamento", "Histórico"],
      selected_tab: "Disponíveis",
      // selected_tab: "Em andamento",
      history: [],
      history_page: 1,
      history_page_is_empty: false,
      start_modal_is_active: false,
      start_modal_type: "short",
      start_modal_data: {},
      stop_modal_is_active: false,
      stop_modal_data: {},
      timeout: null,
      is_loading: false,
      deferCall: false,
      disclaimer_is_active: false,
      trend_modal_is_active: false,
    };
  },

  computed: {
    ...mapGetters({
      getSelectedAccount: "accountTypes/getSelectedAccount",
      getBestsHistoryTotalPages: "bests/getBestsHistoryTotalPages",
      getAvailableBests: "bests/getAvailableBests",
      getUserBests: "bests/getUserBests",
      getConfigs: "config/getConfigs",
    }),
    pageEnabled() {
      if (!this.getConfigs.side_menus) return false;
      return this.getConfigs.side_menus.map((item) => item.menu_name).includes("Bests");
    },
  },

  methods: {
    ...mapActions({
      setBests: "bests/setBests",
      setBestsHistory: "bests/setBestsHistory",
    }),
    start() {
      this.deferCall = true;
      this.selected_tab = "Em andamento";
      this.start_modal_is_active = false;
    },
    async stop() {
      this.is_loading = true;
      setTimeout(async () => {
        await this.setBests(this.getSelectedAccount.account_id);
        this.is_loading = false;
      }, 1000);
      this.stop_modal_is_active = false;
    },
    closeStopModal() {
      this.stop_modal_is_active = false;
    },
    previousPage() {
      if (this.history_page == 1) return;
      this.history_page--;
    },
    nextPage() {
      if (this.history_page_is_empty) return;
      this.history_page++;
    },
  },

  watch: {
    selected_tab: {
      // immediate: true,
      async handler(val) {
        if (val == "Histórico") {
          this.is_loading = true;
          await this.setBestsHistory({
            account_id: this.getSelectedAccount.account_id,
            page: 1,
          });
          this.is_loading = false;
        } else if (this.deferCall) {
          this.is_loading = true;
          setTimeout(async () => {
            await this.setBests(this.getSelectedAccount.account_id);
            this.deferCall = false;
            this.is_loading = false;
          }, 1000);
        } else {
          this.setBests(this.getSelectedAccount.account_id);
        }
      },
    },
    history_page: {
      async handler(val) {
        this.is_loading = true;
        const response = await this.setBestsHistory({
          account_id: this.getSelectedAccount.account_id,
          page: val,
        });
        if (response.length) {
          this.history_page_is_empty = false;
        } else {
          this.history_page_is_empty = true;
        }
        this.is_loading = false;
      },
    },
  },

  async mounted() {
    event_bus.$on("OPEN_TREND_MODAL", (e) => {
      this.trend_modal_is_active = true;
    });
    event_bus.$on("BEST_CALL", (e) => {
      this.start_modal_data = e;
      this.start_modal_is_active = true;
    });
    event_bus.$on("BEST_STOP", (e) => {
      this.stop_modal_data = e;
      this.stop_modal_is_active = true;
    });
    event_bus.$on("BEST_STARTED", () => {
      this.start();
    });
    event_bus.$on("BEST_STOPPED", () => {
      this.stop();
    });

    if (this.getSelectedAccount.account_id) {
      clearTimeout(this.timeout);
      this.is_loading = true;
      await this.setBests(this.getSelectedAccount.account_id);
      this.is_loading = false;
    } else {
      this.is_loading = true;
      this.timeout = setTimeout(async () => {
        await this.setBests(this.getSelectedAccount.account_id);
        this.is_loading = false;
      }, 2000);
    }
  },
};
</script>

<style lang="scss" scoped>
@use "../styles/bests.scss";
</style>
